import { Asset } from '../types';

export type RiskLevel = 'low' | 'moderate' | 'high' | 'very_high';
export type FacilityBadgeKey = 'low' | 'moderate' | 'high' | 'very-high';

/** Shared risk palette; each foreground/background pair exceeds 4.5:1 contrast. */
export const riskColours: Record<RiskLevel, { backgroundColor: string; color: string }> = {
  low: { backgroundColor: '#dcfce7', color: '#166534' },
  moderate: { backgroundColor: '#fef08a', color: '#713f12' },
  high: { backgroundColor: '#fdba74', color: '#7c2d12' },
  very_high: { backgroundColor: '#991b1b', color: '#ffffff' },
};

export interface AssetRiskSummary {
  level: RiskLevel;
  likelihood: number;
  significance: number;
}

export const RISK_LOOKUP: Record<string, RiskLevel> = {
  '5,1': 'moderate', '5,2': 'high', '5,3': 'very_high', '5,4': 'very_high', '5,5': 'very_high',
  '4,1': 'moderate', '4,2': 'high', '4,3': 'high', '4,4': 'very_high', '4,5': 'very_high',
  '3,1': 'low', '3,2': 'moderate', '3,3': 'high', '3,4': 'very_high', '3,5': 'very_high',
  '2,1': 'low', '2,2': 'low', '2,3': 'moderate', '2,4': 'high', '2,5': 'very_high',
  '1,1': 'low', '1,2': 'low', '1,3': 'moderate', '1,4': 'high', '1,5': 'high',
};

export const LEVEL_ORDER: Record<RiskLevel, number> = {
  low: 0,
  moderate: 1,
  high: 2,
  very_high: 3,
};

const RISK_LABELS: Record<RiskLevel, string> = {
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  very_high: 'Very High',
};

const LIKELIHOOD_LETTERS: Record<number, string> = {
  5: 'A', 4: 'B', 3: 'C', 2: 'D', 1: 'E',
};

const areValidCoordinates = (likelihood: number, significance: number): boolean =>
  Number.isInteger(likelihood)
  && Number.isInteger(significance)
  && likelihood >= 1
  && likelihood <= 5
  && significance >= 1
  && significance <= 5;

export const getRiskLevel = (
  likelihood: number,
  significance: number,
): RiskLevel | null => {
  if (!areValidCoordinates(likelihood, significance)) return null;
  return RISK_LOOKUP[`${likelihood},${significance}`] ?? null;
};

export const riskLabel = (level: RiskLevel): string => RISK_LABELS[level];

export const cellCode = (likelihood: number, significance: number): string =>
  areValidCoordinates(likelihood, significance)
    ? `${LIKELIHOOD_LETTERS[likelihood]}${significance}`
    : '';

export const getAssetOwnRisk = (asset: Asset): AssetRiskSummary | null => {
  const likelihood = asset.current_likelihood ?? asset.risk_likelihood;
  const significance = asset.current_consequence ?? asset.risk_significance;
  if (likelihood == null || significance == null) return null;
  const level = getRiskLevel(likelihood, significance);
  return level ? {level, likelihood, significance} : null;
};

export const buildChildrenMap = (assets: Asset[]): Map<string, Asset[]> => {
  const byId = new Set(assets.map((asset) => asset.id));
  const children = new Map<string, Asset[]>();
  assets.forEach((asset) => {
    const parentId = asset.parent_asset_id;
    if (!parentId || !byId.has(parentId)) return;
    const parentChildren = children.get(parentId) ?? [];
    parentChildren.push(asset);
    children.set(parentId, parentChildren);
  });
  return children;
};

export const getAggregatedRiskByAssetId = (
  assets: Asset[],
): Map<string, AssetRiskSummary | null> => {
  const byId = new Map(assets.map((asset): [string, Asset] => [asset.id, asset]));
  const childrenMap = buildChildrenMap(assets);
  const memo = new Map<string, AssetRiskSummary | null>();
  const stack = new Set<string>();

  const dfs = (assetId: string): AssetRiskSummary | null => {
    if (memo.has(assetId)) return memo.get(assetId) ?? null;
    if (stack.has(assetId)) return null;
    const asset = byId.get(assetId);
    if (!asset) return null;

    stack.add(assetId);
    let best = getAssetOwnRisk(asset);
    const children = childrenMap.get(assetId) ?? [];
    children.forEach((child) => {
      const childRisk = dfs(child.id);
      if (childRisk && (!best || riskRank(childRisk) > riskRank(best))) {
        best = childRisk;
      }
    });
    stack.delete(assetId);
    memo.set(assetId, best);
    return best;
  };

  assets.forEach((asset) => dfs(asset.id));
  return memo;
};

export const getFacilityAggregatedRisk = (
  assets: Asset[],
): AssetRiskSummary | null => {
  if (assets.length === 0) return null;
  const assetIds = new Set(assets.map((asset) => asset.id));
  const rootIds = assets
    .filter((asset) => !asset.parent_asset_id || !assetIds.has(asset.parent_asset_id))
    .map((asset) => asset.id);
  const aggregatedById = getAggregatedRiskByAssetId(assets);
  const idsToCheck = rootIds.length > 0 ? rootIds : assets.map((asset) => asset.id);
  let best: AssetRiskSummary | null = null;
  idsToCheck.forEach((id) => {
    const summary = aggregatedById.get(id) ?? null;
    if (summary && (!best || riskRank(summary) > riskRank(best))) {
      best = summary;
    }
  });
  return best;
};

export const toFacilityBadgeKey = (level: RiskLevel): FacilityBadgeKey =>
  level.replace('_', '-') as FacilityBadgeKey;

export const isOpenAction = (task: { status: string }): boolean =>
  task.status !== 'complete' && task.status !== 'cancelled';

export const highestRisk = (ratings: { likelihood?: number | null; consequence?: number | null }[]): AssetRiskSummary | null => {
  let best: AssetRiskSummary | null = null;
  ratings.forEach(({ likelihood, consequence }) => {
    const level = getRiskLevel(likelihood ?? 0, consequence ?? 0);
    if (level && (!best || riskRank({level,likelihood:likelihood!,significance:consequence!}) > riskRank(best))) {
      best = { level, likelihood: likelihood!, significance: consequence! };
    }
  });
  return best;
};

const riskRank = (risk: AssetRiskSummary): number => LEVEL_ORDER[risk.level] * 100 + risk.significance * 10 + risk.likelihood;
